import React, { useState, useEffect } from 'react';
import { MapPin, TrendingUp, Users, Calendar, Flame, Music, Film, Palette, ChevronRight, Zap, Radio, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import { communityAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Link, useSearchParams } from 'react-router-dom';

const GENRE_ICONS = { music: Music, film: Film, art: Palette };

export default function CityHeatmapPage() {
  const [searchParams] = useSearchParams();
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [cityData, setCityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customCity, setCustomCity] = useState('');

  useEffect(() => {
    communityAPI.multiCityHeatmap().then(r => {
      setCities(r.data || []);
      setLoading(false);
      const fromUrl = searchParams.get('city');
      if (fromUrl) {
        const match = (r.data || []).find(c => c.city.toLowerCase() === fromUrl.toLowerCase());
        if (match) { setSelectedCity(match.city); setCityData(match); }
      }
    }).catch(() => setLoading(false));
  }, [searchParams]);

  const selectCity = async (city) => {
    setSelectedCity(city);
    const existing = cities.find(c => c.city === city);
    if (existing) { setCityData(existing); return; }
    try {
      const r = await communityAPI.cityHeatmap(city);
      setCityData(r.data);
    } catch { setCityData(null); }
  };

  const handleCustomSearch = (e) => {
    e.preventDefault();
    if (customCity.trim()) selectCity(customCity.trim());
  };

  const scoreColor = (score) => score >= 70 ? 'text-[#b8ff00]' : score >= 40 ? 'text-amber-400' : 'text-zinc-500';
  const scoreBg = (score) => score >= 70 ? 'from-[#b8ff00]/10 to-emerald-500/5 border-[#b8ff00]/20' : score >= 40 ? 'from-amber-500/10 to-orange-500/5 border-amber-500/20' : 'from-zinc-800 to-zinc-900 border-zinc-700';

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="mb-8">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-xs font-bold mb-3">
            <MapPin size={12} /> City Heatmaps
          </span>
          <h1 className="font-display font-black text-3xl md:text-4xl text-white mb-2">Where's the Energy?</h1>
          <p className="text-zinc-500 text-sm">Compare city activity across artists, projects, events, and funding.</p>
        </div>

        {/* Search */}
        <form onSubmit={handleCustomSearch} className="mb-8 flex items-center gap-2 max-w-md">
          <Input value={customCity} onChange={e => setCustomCity(e.target.value)} placeholder="Search any city..." className="bg-zinc-900 border-zinc-700 text-white" data-testid="heatmap-city-search" />
          <Button type="submit" className="bg-[#b8ff00] text-black font-bold rounded-full px-5" data-testid="heatmap-search-btn">Go</Button>
        </form>

        {/* City Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
          {cities.map((c, idx) => (
            <motion.button
              key={c.city}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              onClick={() => selectCity(c.city)}
              className={`p-4 rounded-xl border text-left transition-all ${selectedCity === c.city ? 'border-[#b8ff00] bg-[#b8ff00]/5' : 'border-zinc-800 bg-zinc-900 hover:border-zinc-600'}`}
              data-testid={`heatmap-city-${c.city}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-display font-bold text-sm text-white">{c.city}</span>
                <span className={`text-xs font-bold ${scoreColor(c.activity_score)}`}>{c.activity_score}</span>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-zinc-500">
                <span className="flex items-center gap-1"><Users size={9} /> {c.artists}</span>
                <span className="flex items-center gap-1"><TrendingUp size={9} /> {c.active_projects}</span>
                <span className="flex items-center gap-1"><Calendar size={9} /> {c.events}</span>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Selected City Detail */}
        {cityData && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className={`rounded-2xl border bg-gradient-to-br p-6 ${scoreBg(cityData.activity_score)}`} data-testid="city-detail">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-display font-black text-2xl text-white">{cityData.city}</h2>
                  <p className="text-xs text-zinc-500">Activity Score</p>
                </div>
                <div className={`text-4xl font-display font-black ${scoreColor(cityData.activity_score)}`} data-testid="city-score">
                  {cityData.activity_score}
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { label: 'Artists', value: cityData.artists, icon: Users, color: 'text-violet-400' },
                  { label: 'Active Projects', value: cityData.active_projects, icon: TrendingUp, color: 'text-emerald-400' },
                  { label: 'Events', value: cityData.events, icon: Calendar, color: 'text-amber-400' },
                  { label: 'Total Funding', value: formatCurrency(cityData.total_funding), icon: Zap, color: 'text-[#b8ff00]' },
                ].map((s, i) => (
                  <div key={s.label} className="p-3 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
                    <s.icon size={14} className={`${s.color} mb-1.5`} />
                    <p className="text-xl font-display font-bold text-white">{s.value}</p>
                    <p className="text-[10px] text-zinc-500">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Genre distribution */}
              {Object.keys(cityData.genre_distribution || {}).length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-zinc-400 mb-2">Genre Distribution</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(cityData.genre_distribution).sort((a, b) => b[1] - a[1]).map(([genre, count]) => {
                      const Icon = GENRE_ICONS[genre] || Award;
                      return (
                        <span key={genre} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-800 border border-zinc-700 text-xs">
                          <Icon size={11} className="text-[#b8ff00]" />
                          <span className="text-zinc-300 capitalize">{genre}</span>
                          <span className="text-zinc-500 font-bold">{count}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Hotspots */}
              {cityData.hotspots?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-zinc-400 mb-2">Venue Hotspots</h3>
                  <div className="space-y-1.5">
                    {cityData.hotspots.map((h, i) => (
                      <div key={h.venue} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-zinc-800/50">
                        <span className="text-[#b8ff00] font-bold text-xs w-4">#{i + 1}</span>
                        <MapPin size={12} className="text-rose-400" />
                        <span className="text-sm text-zinc-300 flex-1">{h.venue}</span>
                        <span className="text-xs text-zinc-500">{h.events} events</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Link to={`/local-scene?city=${encodeURIComponent(cityData.city)}`}>
                  <Button className="bg-[#b8ff00] text-black font-bold rounded-full px-6" data-testid="goto-local-scene">
                    Open Local Scene Board <ChevronRight size={14} className="ml-1" />
                  </Button>
                </Link>
                <Link to="/projects">
                  <Button variant="outline" className="border-zinc-700 text-zinc-300 rounded-full px-6" data-testid="browse-projects">
                    Browse Projects
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
